import {
  buildOrderLiteral,
  buildPagingLiteral,
  buildWhereLiteral,
  OrderBy,
} from '@/lib/graphql-query-builder';

/** The gateway's own order by default: the HR term has no employee name to sort by. */
export const DEFAULT_LEAVE_OVERVIEW_ORDER: OrderBy[] = [];

/** A page of employees' current (`IsCurrentHrYear: true`) HR terms, with their `LeaveAllotments` —
 * what the Leave Overview tab sums Allocated/Taken/Requested/Remaining from. */
export const buildGetHrProfilesForOverviewQuery = (
  where: Record<string, unknown>,
  pageNo: number,
  pageSize: number,
  order: OrderBy[] = DEFAULT_LEAVE_OVERVIEW_ORDER
) => `
  query {
    HrProfiles: getUserHRPeriodicalProfiles(
      where: ${buildWhereLiteral(where)}
      order: ${buildOrderLiteral(order)}
      paging: ${buildPagingLiteral(pageNo, pageSize)}
    ) {
      totalCount
      items {
        ItemId
        UserId
        HRPeriodStartDate
        HRPeriodEndDate
        IsCurrentHrYear
        DepartmentId
        DepartmentName
        EmploymentType
        LeaveCarryForwardDays
        AvgDailyWorkHour
        WorkDays {
          WorkDay
          WorkingHours
          EffectiveFrom
        }
        LeaveAllotments {
          LeaveCategoryId
          LeaveCategory
          LeaveLimitTotalDays
          IsApplicable
          IsUnlimited
          IsDefault
        }
      }
    }
  }
`;

/** Batch-fetches the Approved/Pending leave requests of a page of employees (`UserId: { $in: [...] }`)
 * in one query, instead of one query per employee. */
export const buildGetLeaveRequestsForOverviewQuery = (
  where: Record<string, unknown>,
  pageNo: number,
  pageSize: number
) => `
  query {
    LeaveRequests: getLeaveRequests(
      where: ${buildWhereLiteral(where)}
      order: []
      paging: ${buildPagingLiteral(pageNo, pageSize)}
    ) {
      items {
        UserId
        LeaveCategoryId
        StartDate
        EndDate
        LeaveType
        Status
      }
    }
  }
`;

/** The overtime balance rows (no `Source`) of a page of employees: an "Overtime Leave" has no allotment and
 * is paid from them. */
export const buildGetOvertimeBalancesForOverviewQuery = (userIds: string[]) => `
  query {
    Overtimes: getOvertimes(
      where: ${buildWhereLiteral({ UserId: { $in: userIds }, TotalRemainingOvertimeHourInSecond: { $gt: -1000000000000 } })}
      order: []
      paging: ${buildPagingLiteral(1, Math.max(userIds.length * 2, 10))}
    ) {
      items {
        UserId
        Source
        TotalRemainingOvertimeHourInSecond
      }
    }
  }
`;
